import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Truck, Package, MapPin, Phone, CheckCircle, Clock, Navigation, MessageCircle } from 'lucide-react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { WhatsAppButton, WhatsAppMessages, getWhatsAppUrl } from '../../components/WhatsAppChat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DriverDashboard = () => {
  const { user } = useAuth();
  const [driver, setDriver] = useState(null);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [myDeliveries, setMyDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDriverData();
  }, []);

  const fetchDriverData = async () => {
    try {
      const [driverRes, availableRes, myRes] = await Promise.all([
        axios.get(`${API}/driver/profile`),
        axios.get(`${API}/driver/available-orders`),
        axios.get(`${API}/driver/my-deliveries`)
      ]);
      setDriver(driverRes.data);
      setAvailableOrders(availableRes.data);
      setMyDeliveries(myRes.data);
    } catch (error) {
      console.error('Failed to fetch driver data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAvailability = async (checked) => {
    try {
      await axios.put(`${API}/driver/availability?is_available=${checked}`);
      setDriver({ ...driver, is_available: checked });
      toast.success(`You are now ${checked ? 'available' : 'unavailable'} for deliveries`);
    } catch (error) {
      toast.error('Failed to update availability');
    }
  };

  const acceptOrder = async (orderId) => {
    try {
      await axios.put(`${API}/driver/accept-order/${orderId}`);
      toast.success('Order accepted!');
      fetchDriverData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept order');
    }
  };

  const rejectOrder = async (orderId) => {
    try {
      await axios.put(`${API}/driver/reject-order/${orderId}`);
      toast.info('Order declined - assigned to next driver');
      fetchDriverData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject order');
    }
  };

  const updateDeliveryStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/driver/update-delivery/${orderId}`, { status });
      toast.success(`Delivery marked as ${status}`);
      fetchDriverData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-ZA', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  const activeDelivery = myDeliveries.find(d => ['out_for_delivery', 'picked_up', 'in_transit'].includes(d.status));

  return (
    <div className="min-h-screen flex flex-col bg-stone-50" data-testid="driver-dashboard">
      <Navbar />
      
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Driver Header */}
          <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-green-900 flex items-center justify-center">
                  <Truck className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-stone-900" data-testid="driver-name">
                    {user?.name}
                  </h1>
                  <p className="text-stone-600">{driver?.vehicle_type} - {driver?.vehicle_number}</p>
                  <div className="flex items-center gap-4 mt-1 text-sm text-stone-500">
                    <span>{driver?.total_deliveries} deliveries</span>
                    <span>★ {driver?.rating.toFixed(1)}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <span className="text-stone-600">Available</span>
                <Switch
                  checked={driver?.is_available}
                  onCheckedChange={toggleAvailability}
                  data-testid="availability-toggle"
                />
              </div>
            </div>
          </div>

          {/* Active Delivery */}
          {activeDelivery && (
            <div className="bg-orange-50 border-l-4 border-orange-500 rounded-xl p-6 mb-6" data-testid="active-delivery">
              <h2 className="text-lg font-bold text-orange-800 mb-4 flex items-center gap-2">
                <Navigation className="w-5 h-5" />
                Active Delivery
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-orange-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-stone-900 text-lg">{activeDelivery.delivery_address}</p>
                    <p className="text-stone-600">{activeDelivery.delivery_phone}</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {activeDelivery.status === 'out_for_delivery' && (
                    <Button
                      onClick={() => updateDeliveryStatus(activeDelivery.id, 'picked_up')}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="mark-picked-up"
                    >
                      Mark Picked Up
                    </Button>
                  )}
                  {activeDelivery.status === 'picked_up' && (
                    <Button
                      onClick={() => updateDeliveryStatus(activeDelivery.id, 'in_transit')}
                      className="bg-orange-600 hover:bg-orange-700"
                      data-testid="mark-in-transit"
                    >
                      Start Delivery
                    </Button>
                  )}
                  {activeDelivery.status === 'in_transit' && (
                    <Button
                      onClick={() => updateDeliveryStatus(activeDelivery.id, 'delivered')}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="mark-delivered"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Mark Delivered
                    </Button>
                  )}
                </div>

                {/* WhatsApp Customer Communication */}
                <div className="pt-4 border-t border-orange-200 space-y-3">
                  <p className="text-sm text-orange-700">
                    {activeDelivery.items.length} items • R{activeDelivery.total.toFixed(2)}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <WhatsAppButton
                      phone={activeDelivery.delivery_phone}
                      message={WhatsAppMessages.deliveryUpdate(activeDelivery.id.slice(0, 8), activeDelivery.delivery_address)}
                      label="Message Customer"
                      size="sm"
                      className="text-sm"
                    />
                    {activeDelivery.status === 'in_transit' && (
                      <WhatsAppButton
                        phone={activeDelivery.delivery_phone}
                        message={WhatsAppMessages.deliveryArrived(activeDelivery.id.slice(0, 8))}
                        label="I've Arrived"
                        size="sm"
                        className="text-sm bg-green-500 hover:bg-green-600"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Available Orders */}
          {driver?.is_available && !activeDelivery && (
            <div className="mb-6">
              <h2 className="text-lg font-bold text-stone-900 mb-4 flex items-center gap-2">
                <Package className="w-5 h-5" />
                Available Orders
              </h2>
              
              {availableOrders.length === 0 ? (
                <div className="bg-white rounded-xl p-8 text-center text-stone-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                  <p>No orders available right now</p>
                  <p className="text-sm">Check back in a few minutes</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {availableOrders.map((order) => (
                    <div
                      key={order.id}
                      className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-green-500"
                      data-testid={`available-order-${order.id}`}
                    >
                      <div className="flex flex-col sm:flex-row justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex items-start gap-2">
                            <MapPin className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold text-stone-900">{order.delivery_address}</p>
                              <p className="text-sm text-stone-600">{order.delivery_phone}</p>
                            </div>
                          </div>
                          <p className="text-sm text-stone-500">
                            {order.items.length} items • R{order.total.toFixed(2)} • {formatDate(order.created_at)}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => acceptOrder(order.id)}
                            className="bg-green-900 hover:bg-green-800"
                            data-testid={`accept-order-${order.id}`}
                          >
                            Accept Order
                          </Button>
                          <Button
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => rejectOrder(order.id)}
                            data-testid={`reject-order-${order.id}`}
                          >
                            Decline
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Recent Deliveries */}
          <div>
            <h2 className="text-lg font-bold text-stone-900 mb-4">Recent Deliveries</h2>
            {myDeliveries.filter(d => d.status === 'delivered').length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center text-stone-500">
                <Truck className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                <p>No completed deliveries yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {myDeliveries
                  .filter(d => d.status === 'delivered')
                  .slice(0, 10)
                  .map((delivery) => (
                    <div
                      key={delivery.id}
                      className="bg-white rounded-lg p-4 shadow-sm flex justify-between items-center"
                    >
                      <div>
                        <p className="font-medium text-stone-900">{delivery.delivery_address}</p>
                        <p className="text-sm text-stone-500">
                          {delivery.items.length} items • {formatDate(delivery.updated_at)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">R{delivery.total.toFixed(2)}</p>
                        <span className="badge-success text-xs">Delivered</span>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DriverDashboard;
